import * as p from '@clack/prompts'
import { spinner } from '@core-engine/utils/spinner.js'
import pc from 'picocolors'

import { parseDotenvFile } from './env-pull.js'
import { parseVercelJson, runVercel } from './runner.js'
import type { NeonProvisionResult, VercelRunner } from './types.js'

/** Non-interactive provisioning can't wait on a human; keep it bounded. */
const PROVISION_TIMEOUT_MS = 180_000
/** Interactive provisioning waits on the user clicking through Vercel's prompts. */
const INTERACTIVE_PROVISION_TIMEOUT_MS = 600_000

interface NeonResourceMeta {
  dashboardUrl?: string
  ssoUrl?: {
    resource?: string
  }
}

/**
 * Provision a Neon Postgres database resource via `vercel integration add neon`
 * when a human is present. Tries a quiet, captured-output JSON attempt first so
 * Vercel's banner and progress output stay off the terminal.
 * Only when that attempt fails does it rerun attached to the terminal so the
 * user can accept terms and pick the Free plan in Vercel's own prompts.
 *
 * The Vercel CLI owns any marketplace/project association during `integration
 * add`; BetterStart only temp-pulls DATABASE_URL afterwards.
 * Callers must stop any active spinner first — inherited stdio and a clack
 * spinner fight over the TTY.
 */
export async function provisionNeonInteractive(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const quietSpinner = spinner()
  quietSpinner.start('Creating your Neon database')
  const quiet = await runVercel(runner, neonAddArgs(options), {
    cwd,
    mode: 'capture',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (quiet.success) {
    quietSpinner.clear()
    return readNeonProvisionInfo(quiet.stdout)
  }

  // The quiet attempt cannot answer marketplace-terms or plan prompts; erase
  // the spinner (it would fight the inherited stdio) and let the user click
  // through Vercel's own prompts on a fresh account.
  quietSpinner.clear()
  p.log.info(
    `Create your Neon database in the Vercel prompts below ${pc.dim(
      '(the Free plan is recommended).'
    )}`
  )

  const add = await runVercel(runner, connectedNeonAddArgs(options), {
    cwd,
    mode: 'inherit',
    timeoutMs: INTERACTIVE_PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    return { failure: add.timedOut ? 'timeout' : 'provision-failed' }
  }

  return { usedTerminalFallback: true }
}

/**
 * Non-interactive provisioning for `--yes`/CI with a `VERCEL_TOKEN`. Runs
 * `integration add neon --format json`; on a fresh account this can fail when
 * marketplace terms or a plan selection are required (no human to confirm), so
 * the captured output is returned as `detail` for debugging. A `--plan` id is
 * usually required for this path to succeed unattended.
 */
export async function provisionNeon(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const provisionSpinner = spinner()
  provisionSpinner.start('Creating your Neon database')

  const add = await runVercel(runner, neonAddArgs(options), {
    cwd,
    mode: 'capture',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    provisionSpinner.stop('Neon provisioning did not complete')
    const combined = `${add.stdout}\n${add.stderr}`.trim()
    const detail = combined || undefined
    if (/terms/i.test(combined)) return { failure: 'terms-required', detail }
    if (/claim/i.test(combined)) return { failure: 'claim-required', detail }
    return { failure: add.timedOut ? 'timeout' : 'provision-failed', detail }
  }

  provisionSpinner.clear()
  return readNeonProvisionInfo(add.stdout)
}

/**
 * Extract the Postgres connection string from a pulled dotenv file. The Vercel
 * Neon integration emits the standard `DATABASE_URL` and pooled `POSTGRES_URL`
 * keys; ignore speculative payload shapes.
 */
export function readDbUrlFromDotenv(filePath: string): string | undefined {
  const vars = parseDotenvFile(filePath)

  const isPg = (v: string | undefined): v is string =>
    !!v && (v.startsWith('postgres://') || v.startsWith('postgresql://'))

  if (isPg(vars.get('DATABASE_URL'))) return vars.get('DATABASE_URL')
  if (isPg(vars.get('POSTGRES_URL'))) return vars.get('POSTGRES_URL')
  return undefined
}

export function readNeonProvisionInfo(stdout: string): NeonProvisionResult {
  const meta = parseVercelJson<NeonResourceMeta>(stdout)
  if (!meta) return {}

  const info: NeonProvisionResult = {}
  if (meta.dashboardUrl) info.dashboardUrl = meta.dashboardUrl
  if (meta.ssoUrl?.resource) info.resourceUrl = meta.ssoUrl.resource
  return info
}

function neonAddArgs(options: { plan?: string }): string[] {
  const addArgs = ['integration', 'add', 'neon', '--format', 'json']
  if (options.plan) addArgs.push('--plan', options.plan)
  return addArgs
}

function connectedNeonAddArgs(options: { plan?: string }): string[] {
  const addArgs = ['integration', 'add', 'neon']
  if (options.plan) addArgs.push('--plan', options.plan)
  return addArgs
}
